import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';

type UIMode = 'current' | 'experimental';

interface UIContextType {
  uiMode: UIMode;
  isV2Mode: boolean;
  isV2BetaUser: boolean;
  showUIToggle: boolean;
  toggleUIMode: () => void;
  setUIMode: (mode: UIMode) => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);
const UI_MODE_STORAGE_KEY = 'uiMode';

const isFlagEnabled = (value: unknown) => value === true || value === 'true' || value === '1';

const normalizeUIMode = (mode: string | null): UIMode | null => {
  if (mode === 'experimental' || mode === 'v2') return 'experimental';
  if (mode === 'current' || mode === 'legacy') return 'current';
  return null;
};

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider = ({ children }: UIProviderProps) => {
  const { user } = useAuth();
  const globalV2BetaFlag = isFlagEnabled(import.meta.env.VITE_ENABLE_V2_BETA);
  const globalV2ToggleFlag = isFlagEnabled(import.meta.env.VITE_ENABLE_V2_TOGGLE);
  const userMetadata = user?.user_metadata;
  const betaFeatures = Array.isArray(userMetadata?.beta_features)
    ? userMetadata.beta_features
    : [];
  const isV2BetaUser = Boolean(
    globalV2BetaFlag ||
    userMetadata?.v2_beta ||
    userMetadata?.is_v2_beta ||
    betaFeatures.includes('v2')
  );
  const showUIToggle = isV2BetaUser || globalV2ToggleFlag;

  const [uiMode, setUIModeState] = useState<UIMode>(() => {
    const savedMode = normalizeUIMode(localStorage.getItem(UI_MODE_STORAGE_KEY));
    return savedMode ?? (globalV2BetaFlag ? 'experimental' : 'current');
  });

  useEffect(() => {
    const savedMode = normalizeUIMode(localStorage.getItem(UI_MODE_STORAGE_KEY));
    if (!savedMode && isV2BetaUser) {
      setUIModeState('experimental');
      localStorage.setItem(UI_MODE_STORAGE_KEY, 'experimental');
    }
  }, [isV2BetaUser]);
  
  const setUIMode = (mode: UIMode) => {
    setUIModeState(mode);
    localStorage.setItem(UI_MODE_STORAGE_KEY, mode);
  };

  const toggleUIMode = () => {
    const newMode = uiMode === 'current' ? 'experimental' : 'current';
    setUIMode(newMode);
  };
  
  return (
    <UIContext.Provider
      value={{
        uiMode,
        isV2Mode: uiMode === 'experimental',
        isV2BetaUser,
        showUIToggle,
        toggleUIMode,
        setUIMode,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
